import type { AtAGlanceOutput, AtAGlanceOverviewItem } from './atAGlanceTypes.ts';

const MAX_OVERVIEW_ITEMS = 3;
const MAX_SUMMARIES = 30;
const MAX_ARI_LENGTH = 1_000;
const MAX_OUTPUT_TEXT_LENGTH = 500;
const MAX_LABEL_LENGTH = 40;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const isNonEmptyString = (value: unknown, limit: number): value is string =>
	typeof value === 'string' && value.trim().length > 0 && value.length <= limit;

const adaptOverviewItem = (value: string): AtAGlanceOverviewItem => {
	const normalized = value.replace(/\*\*/g, '').trim();
	const separatorIndex = normalized.indexOf(': ');
	if (separatorIndex <= 0 || separatorIndex > MAX_LABEL_LENGTH) {
		return { label: '', text: normalized, evidence: [] };
	}

	return {
		label: normalized.slice(0, separatorIndex),
		text: normalized.slice(separatorIndex + 2),
		evidence: [],
	};
};

/**
 * Converts the pre-status cc-smarts response into the current UI model during
 * the frontend/backend rollout window.
 *
 * TODO: Remove this adapter once cc-smarts no longer serves the legacy
 * string-overview response in production.
 */
export const adaptLegacyAtAGlanceOutput = (
	value: Record<string, unknown>,
): AtAGlanceOutput | null => {
	const { overview, summaries } = value;
	if ('status' in value || !Array.isArray(overview) || !Array.isArray(summaries)) {
		return null;
	}

	const parsedOverview: AtAGlanceOverviewItem[] = [];
	const seenOverview = new Set<string>();
	for (const item of overview) {
		if (!isNonEmptyString(item, MAX_OUTPUT_TEXT_LENGTH)) {
			continue;
		}
		const normalized = item.trim();
		if (seenOverview.has(normalized)) {
			continue;
		}
		seenOverview.add(normalized);
		parsedOverview.push(adaptOverviewItem(normalized));
		if (parsedOverview.length === MAX_OVERVIEW_ITEMS) {
			break;
		}
	}

	const parsedSummaries: AtAGlanceOutput['summaries'] = [];
	for (const summary of summaries) {
		if (!isRecord(summary)) {
			continue;
		}
		if (
			!isNonEmptyString(summary.ari, MAX_ARI_LENGTH) ||
			!isNonEmptyString(summary.title, MAX_OUTPUT_TEXT_LENGTH) ||
			!isNonEmptyString(summary.oneLiner, MAX_OUTPUT_TEXT_LENGTH)
		) {
			continue;
		}
		parsedSummaries.push({
			ari: summary.ari.trim(),
			title: summary.title.trim(),
			oneLiner: summary.oneLiner.trim(),
			evidence: [],
		});
		if (parsedSummaries.length === MAX_SUMMARIES) {
			break;
		}
	}

	return {
		status: 'GENERATED',
		overview: parsedOverview,
		summaries: parsedSummaries,
	};
};
